import { Spinner } from '@giftgroup/ui';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { commonCopy } from '../copy/common';
import { ApiError } from '../lib/api';
import { useMe } from '../lib/session';
import s from './shell.module.css';

/**
 * Route guards, in the order doc 05 sets out:
 *   1. no session        → /welcome?next=…
 *   2. archived account  → /restore-account
 *   3. no family         → /start
 *   4. stale active family → the first family
 */
export function RequireSession() {
  const location = useLocation();
  const { data: me, isPending, error } = useMe();

  if (isPending) return <RouteSpinner />;

  if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/welcome?next=${encodeURIComponent(next)}`} replace />;
  }
  if (error) throw error;

  if (me?.user.isArchived && location.pathname !== '/restore-account')
    return <Navigate to="/restore-account" replace />;

  return <Outlet />;
}

/** Everything inside the app shell also needs a family to show. */
export function RequireFamily({ children }: { children?: ReactNode }) {
  const { data: me, isPending } = useMe();

  if (isPending) return <RouteSpinner />;
  if (!me) return <Navigate to="/welcome" replace />;
  if (me.families.length === 0) return <Navigate to="/start" replace />;

  return <>{children ?? <Outlet />}</>;
}

/** Someone who already has a family shouldn't sit on the onboarding fork. */
export function RedirectIfOnboarded() {
  const { data: me, isPending } = useMe();
  if (isPending) return <RouteSpinner />;
  if (me && me.families.length > 0) return <Navigate to="/home" replace />;
  return <Outlet />;
}

export function RouteSpinner() {
  return (
    <div className={s.routeLoading}>
      <Spinner label={commonCopy.loading} />
    </div>
  );
}
